<script setup>
import { ref, computed } from 'vue'
import TableList from '../others/TableList.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import Commons from '@/shared/utils/commons'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import ProductFormModal from './ProductFormModal.vue'

const showModal = ref(false)
const editingProduct = ref(null)
const deleteTriggerRef = ref(null)
const toggleTriggerRef = ref(null)

const openCreate = () => {
  editingProduct.value = null
  showModal.value = true
}

const openEdit = product => {
  editingProduct.value = { ...product }
  showModal.value = true
}

const onDelete = async product => {
  const res = await window.customPrompt({
    title: `Supprimer le produit ?`,
    description: `Êtes-vous sûr de vouloir supprimer le produit "${product.name}" ?`,
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Annuler',
  })

  if (res.accepted) {
    deleteTriggerRef.value?.trigger({ id: product.id })
  }
}

const onToggleStatus = async product => {
  const action = product.isActive ? 'deactivateProduct' : 'activateProduct'
  const actionText = product.isActive ? 'Désactiver' : 'Activer'
  
  const res = await window.customPrompt({
    title: `${actionText} le produit ?`,
    description: `Êtes-vous sûr de vouloir ${actionText.toLowerCase()} le produit "${product.name}" ?`,
    formFields: [],
    successText: actionText,
    cancelText: 'Annuler',
  })

  if (res.accepted) {
    toggleTriggerRef.value?.trigger({ id: product.id, _action: action })
  }
}

const onSuccess = msg => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: msg,
    type: 'success',
  })
  showModal.value = false
}

const onError = err => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: err?.message || 'Une erreur est survenue',
    type: 'error',
  })
}

const cols = computed(() => [
  {
    name: 'Produit',
    id: 'name',
    image: el => el.image || null,
    value(el) {
      return [
        el.name ? `<div class="truncate max-w-[300px] overflow-hidden ">${el.name}</div>` : '-',
        el.description ? `<div class="truncate max-w-[300px] overflow-hidden ">${el.description}</div>` : '-',
      ]
    },
  },
  {
    name: 'Cible',
    id: 'target',
    filter: {
      key: 'target',
      default: '',
      options: [
        { name: 'Tous', value: '' },
        { name: 'Livreur', value: 'rider' },
        { name: 'Restaurant', value: 'restaurant' },
      ],
    },
    value(el) {
      if (el.target === 'rider') {
        return `<span class="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-bold uppercase bg-purple-50 text-purple-600">Livreur</span>`
      }
      return `<span class="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-bold uppercase bg-blue-50 text-blue-600">Restaurant</span>`
    },
  },
  {
    name: 'Catégorie',
    id: 'category',
    value(el) {
      return el.category || '—'
    },
  },
  {
    name: 'Prix',
    id: 'price',
    value(el) {
      return `<span class="font-bold text-sm text-gray-900">${Commons.formatAmount(el.price)} ${el.currency}</span>`
    },
  },
  {
    name: 'Stock',
    id: 'stock',
    value(el) {
      const cls =
        el.stock <= 0
          ? 'text-rose-500'
          : el.stock < 10
            ? 'text-amber-500'
            : 'text-gray-700'
      return `<span class="text-sm font-semibold ${cls}">${el.stock}</span>`
    },
  },
  {
    name: 'Statut',
    id: 'isActive',
    filter: {
      key: 'isActive',
      default: '',
      options: [
        { name: 'Tous', value: '' },
        { name: 'Actifs', value: 'true' },
        { name: 'Inactifs', value: 'false' },
      ],
    },
    value(el) {
      if (el.isActive) {
        return `<span class="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase bg-green-50 text-green-600 border-green-200">Actif</span>`
      }
      return `<span class="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase bg-gray-50 text-gray-500 border-gray-200">Inactif</span>`
    },
  },
  {
    name: '',
    id: 'action',
    icon: () => '/images/icons/dots.png',
    options(el) {
      return [
        { name: 'Modifier', value: 'edit', icon: 'pi pi-pencil' },
        {
          name: el.isActive ? 'Désactiver' : 'Activer',
          value: 'toggle',
          icon: 'pi pi-power-off',
        },
        { name: 'Supprimer', value: 'delete', icon: 'pi pi-trash' },
      ]
    },
    onClick(opt, el) {
      const action = typeof opt === 'object' ? opt.value : opt
      if (action === 'edit') openEdit(el)
      else if (action === 'toggle') onToggleStatus(el)
      else if (action === 'delete') onDelete(el)
    },
    value() {},
  },
])
</script>

<template>
  <div class="p-5 h-full overflow-auto">
    <TableList
      title="Catalogue Produits Rapidoo"
      dataKey="adminProducts"
      hasSearch
      :cols="cols"
      :refreshOnEvents="['PRODUCTS_UPDATED']"
      :buttons="[{ title: '+ Ajouter un produit', onClick: openCreate }]"
    />

    <!-- Modals & Triggers -->
    <ProductFormModal
      v-if="showModal"
      :product="editingProduct"
      @close="showModal = false"
      @success="onSuccess('Produit enregistré avec succès')"
      @error="onError"
    />

    <ActionTrigger
      dataKey="deleteProduct"
      ref="deleteTriggerRef"
      @success="() => onSuccess('Produit supprimé')"
      @failed="onError"
      dispatchOnSuccess="PRODUCTS_UPDATED"
    />
    <ActionTrigger
      dataKey="activateProduct"
      ref="toggleTriggerRef"
      @success="() => onSuccess('Statut mis à jour')"
      @failed="onError"
      dispatchOnSuccess="PRODUCTS_UPDATED"
    />
  </div>
</template>
